import React from 'react';
import { Chip, Button, Tooltip, Box } from '@mui/material';
import { Launch as LaunchIcon } from '@mui/icons-material';
import { DataManagementConfig, TableColumn } from '@shared/components/data-management/types';
import { SurveyTableData } from '../types';
import { useClosedSurveysForDataManagement } from '../hooks/useClosedSurveysForDataManagement';
import { useDeleteSurvey, useReopenSurvey } from '../hooks/useSurveys';
import { notificationService } from '@shared/components/notifications/notificationService';

/**
 * Creates table configuration for CLOSED surveys
 * These are surveys that have been closed and are no longer accepting responses
 */
export const createClosedSurveysConfig = (
  subjectId: string,
  tenantId: string,
  domainId: string,
  navigate?: (path: string) => void,
  reopenSurveyMutation?: ReturnType<typeof useReopenSurvey>
): DataManagementConfig<SurveyTableData> => {
  const useClosedSurveysQuery = () => useClosedSurveysForDataManagement(subjectId);
  const useDeleteSurveyWrapper = () => useDeleteSurvey(subjectId);

  // Reopen Survey action
  const reopenSurveyAction = {
    label: 'Reopen',
    onClick: async (row: SurveyTableData) => {
      if (!reopenSurveyMutation) {
        console.error('Reopen survey mutation not provided');
        return;
      }
      try {
        await reopenSurveyMutation.mutateAsync(row.id);
      } catch (error) {
        console.error('Failed to reopen survey:', error);
        notificationService.error('Failed to reopen survey. Please try again.');
      }
    },
    color: 'primary' as const,
    variant: 'outlined' as const,
  };

  return {
    hooks: {
      useQuery: useClosedSurveysQuery,
      useDelete: useDeleteSurveyWrapper,
    },

    table: {
      title: 'Closed Surveys',
      columns: [
        {
          label: 'Title',
          key: 'title',
          sx: {
            minWidth: '25%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
        {
          label: 'Status',
          key: 'status',
          render: () => <Chip label="Closed" color="default" variant="outlined" size="small" />,
        },
        {
          label: 'Go to survey',
          key: 'id',
          render: () => (
            <Tooltip title="Survey must be ACTIVE to create instances">
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<LaunchIcon />}
                  disabled
                  sx={{ minWidth: 'auto' }}
                >
                  Go to survey
                </Button>
              </Box>
            </Tooltip>
          ),
        },
        {
          label: 'Created',
          key: 'createdAt',
          render: (value: string) => new Date(value).toLocaleDateString(),
        },
      ] as TableColumn<SurveyTableData>[],
      emptyMessage: '🔒 No closed surveys!\nSurveys you manually close will appear here.',
      errorMessage: 'Failed to load closed surveys. Please try again.',
    },

    actions: {
      // delete: {
      //   label: 'Delete',
      //   confirmTitle: 'Delete Survey',
      //   confirmMessage: (item: SurveyTableData) =>
      //     `Are you sure you want to delete the survey "${item.title}"? This action cannot be undone. Note: Surveys with instances cannot be deleted.`,
      // },
      custom: [reopenSurveyAction],
    },

    features: {
      search: true,
      pagination: true,
    },
  };
};
